import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { User } from '@apis'
import { Helper } from '@utils'
import Column from '@components/column'
import SectionHeading from '@commons/headings/section'
import { AuthSchema } from '@validations'
import Fields from '@components/fields'
import { Form, Formik } from 'formik'
import PasswordCard from '@commons/userDetails/password'
import Button from '@components/button'
import Documents from '@commons/userDetails/documents'
import ProviderProfileDetails from '@commons/userDetails/providerPersonalDetails/profileDetails'

const InstituteHealthProviderFormPage = () => {
  const navigate = useNavigate()
  const { slug } = useParams()
  const [loading, setLoading] = useState<boolean>(false)
  const { data: user } = useSelector((state: any) => state.user)
  const { data } = useSelector((state: any) => state.provider)
  const { state } = useLocation()
  const provider = state?.id ? state : data
  const id = Helper.getIdFromSlug(slug)
  const [initialValues, setInitialValues] = useState<any>({
    name: '',
    email: '',
    phoneNo: '',
    countryCode: 977,
    address: '',
    qualification: '',
    gender: '',
    yos: '',
    avatar: '',
    coverImage: '',
    specializations: [],
    staffRole: '',
    category: '',
    identifications: [
      {
        image: '',
        name: 'Citizenship',
        number: '',
      },
      {
        image: '',
        name: 'Passport',
        number: '',
      },
      {
        image: '',
        name: 'National Id',
        number: '',
      },
    ],
    licenses: [],
    signature: '',
    password: '',
  })

  const fetchData = async () => {
    await User.find({
      state: 'provider',
      id,
    }).catch(() => {})
  }
  const submitHandler = async (values: any) => {
    const payload: any = {
      name: values?.name,
      infos: {
        gender: values?.gender,
        qualification: values?.qualification,
        yos: values?.yos,
        address: values?.address,
        staffRole: values?.staffRole,
        category: values?.category,
        parentId: user?.id,
      },
      role: 'provider',
    }
    if (!id) {
      payload.phoneNo = values?.phoneNo
      payload.countryCode = values?.countryCode
      payload.email = values?.email
      payload.password = values?.password || Helper.generateRandomPassword()
    }
    const identifications = values?.identifications?.filter(
      (document) => !!document?.image?.base64 && !!document?.number,
    )

    if (!!identifications?.length) {
      payload.identifications = identifications?.map((document: any) => {
        return {
          ...document,
          image: {
            base64: document?.image?.base64,
            extension: document?.image?.extension,
          },
        }
      })
    }

    if (!!values?.licenses?.length) {
      payload.licenses = values?.licenses?.map((document: any) => {
        return {
          ...document,
          image: {
            base64: document?.image?.base64,
            extension: document?.image?.extension,
          },
        }
      })
    }

    if (!!values?.signature) {
      payload.signature = [
        {
          name: 'Signature',
          image: {
            base64: values?.signature?.base64,
            extension: values?.signature?.extension,
          },
        },
      ]
    }

    if (values?.avatar?.base64) {
      payload.avatar = {
        base64: values?.avatar?.base64,
        extension: values?.avatar?.extension,
      }
    }
    if (values?.coverImage?.base64) {
      payload.infos = {
        ...payload.infos,
        coverImage: {
          base64: values?.coverImage?.base64,
          extension: values?.coverImage?.extension,
        },
      }
    }

    if (!!values?.specializations?.length) {
      payload.specializations = values.specializations?.map(
        (specialization) => specialization.id,
      )
    }
    setLoading(true)
    if (!!id) {
      await User.update({
        data: payload,
        id,
        state: 'instituteHealthProviders',
      })
        .then(() => {
          navigate('/institution/staffs?tab=health-providers')
        })
        .catch(() => {})
    } else {
      await User.create({
        data: payload,
        action: 'prepend',
        state: 'instituteHealthProviders',
      })
        .then(() => {
          navigate('/institution/staffs?tab=health-providers')
        })
        .catch(() => {})
    }

    setLoading(false)
  }

  useEffect(() => {
    if (!state?.id && !!id) {
      fetchData()
    }
    const identifications = provider?.documents?.filter(
      (document: any) => document?.type == 'Identification',
    )
    const citizenship = identifications?.findIndex(
      (document) => document?.infos?.name == 'Citizenship',
    )
    const passport = identifications?.findIndex(
      (document) => document?.infos?.name == 'Passport',
    )
    const nationalId = identifications?.findIndex(
      (document) => document?.infos?.name == 'National Id',
    )
    const licenses = provider?.documents?.filter(
      (document: any) => document?.type == 'License',
    )
    const signature = provider?.documents?.find(
      (document: any) => document?.type == 'Signature',
    )
    if (provider?.id) {
      setInitialValues({
        name: provider?.name,
        email: provider?.email,
        phoneNo: provider?.phoneNo,
        countryCode: provider?.countryCode,
        address: provider?.infos?.address,
        qualification: provider?.infos?.qualification,
        gender: provider?.infos?.gender,
        yos: provider?.infos?.yos,
        specializations: provider?.specializations,
        avatar: provider?.avatar || '',
        coverImage: provider?.coverImage || '',
        category: provider?.infos?.category,
        staffRole: provider?.infos?.staffRole,
        identifications:
          identifications?.length >= 3
            ? identifications
            : [
                !!identifications?.[citizenship]
                  ? {
                      ...identifications[citizenship]?.infos,
                      oldPath: identifications[citizenship]?.infos?.image,
                      id: identifications[citizenship]?.id,
                    }
                  : {
                      image: '',
                      name: 'Citizenship',
                      number: '',
                    },
                !!identifications?.[passport]
                  ? {
                      ...identifications[passport]?.infos,
                      oldPath: identifications[passport]?.infos?.image,
                      id: identifications[passport]?.id,
                    }
                  : {
                      image: '',
                      name: 'Passport',
                      number: '',
                    },
                !!identifications?.[nationalId]
                  ? {
                      ...identifications[nationalId]?.infos,
                      oldPath: identifications[nationalId]?.infos?.image,
                      id: identifications[nationalId]?.id,
                    }
                  : {
                      image: '',
                      name: 'National Id',
                      number: '',
                    },
              ],
        licenses: !!licenses?.length
          ? licenses?.map((license: any) => {
              return {
                id: license?.id,
                name: license?.infos?.name,
                image: license?.infos?.image,
                oldPath: license?.infos?.image,
              }
            })
          : [],
        signature: !!signature?.id ? signature?.infos?.image : null,
      })
    }
  }, [id, provider?.id])
  return (
    <Column rowGap={20}>
      <SectionHeading title={!!id ? 'Update Provider' : 'Adding Provider'} />
      <Column>
        <Formik
          initialValues={initialValues}
          onSubmit={submitHandler}
          validationSchema={(!!id
            ? AuthSchema.updateValidationSchema
            : AuthSchema.createValidationSchema
          ).concat(AuthSchema.providerValidationSchema)}
          enableReinitialize
        >
          {({ handleSubmit, setFieldValue, values, errors }) => {
            return (
              <Form onSubmit={handleSubmit}>
                <Column rowGap={20}>
                  <Column styleColumn="margin-bottom:60px;position:relative;">
                    <Fields.Upload
                      varient="banner"
                      name="coverImage"
                      size={250}
                    />
                    <Fields.Upload
                      varient="avatar"
                      name="avatar"
                      size={80}
                      styleWrapper="position:absolute;bottom:-40px;"
                    />
                  </Column>
                  <ProviderProfileDetails
                    setFieldValue={setFieldValue}
                    values={values}
                    errors={errors}
                  />
                  <Documents
                    identifications={values?.identifications}
                    licenses={values?.licenses}
                    setFieldValue={setFieldValue}
                    role="doctor"
                  />
                  {!id && <PasswordCard />}
                  <Column alignItems="flex-end">
                    <Button
                      type="submit"
                      title="Save"
                      height={45}
                      loading={loading}
                    />
                  </Column>
                </Column>
              </Form>
            )
          }}
        </Formik>
      </Column>
    </Column>
  )
}

export default InstituteHealthProviderFormPage
